import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { roomsAPI, bookingsAPI, teamsAPI } from "../../services/meetingsAPI";
import { mergeUserWithStaffSession, resolveStaffDetailsForBooking } from "../../utils/companyStaffSession";
import {
  isMeetingRoomBookableByUser,
  isMeetingRoomOwnedByUser,
  isPublicMeetingRoom,
  isSameCompanyMeetingRoom,
} from "../../utils/meetingRoomVisibility";
import { toast } from "react-toastify";
import { getPropertyImage } from "../../utils/imageHelper";
import "./BookRoom.css";

const MEETING_ROOM_FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&q=80";

// Suggested members when "Add Member" is clicked (real emails so booking notifications can be sent)
const SUGGESTED_MEMBERS = [
  { name: "Shaima", email: "shaimakunthoor2007@gmail.com" },
  { name: "James", email: "james.dekkampady@gmail.com" },
  { name: "Lavanya", email: "lavanyaraiks@gmail.com" },
  { name: "Chaithra", email: "chaithra.ajana@gmail.com" },
  { name: "Chaithra P", email: "chaithradhanduguri@gmail.com" },
  { name: "Haseena", email: "adrahaseena26@gmail.com" },
  { name: "Mubeena", email: "kmubeena895@gmail.com" },
  { name: "Thafseeda", email: "thafseeda2003@gmail.com" },
  { name: "Sowjanya", email: "sowjanyapr5@gmail.com" },
  { name: "Sharan", email: "sharangowda448@gmail.com" },
  { name: "Sincan", email: "sinchankadaba@gmail.com" },
  { name: "Sudeep", email: "sudeepkalpure@gmail.com" },
  { name: "Reena", email: "reenapa2010@gmail.com" },
  { name: "Tiju", email: "tijukariyil@gmail.com" },
  { name: "Navya", email: "navyanavyan54@gmail.com" },
  { name: "Prshanth", email: "nithishk6765@gmail.com" },
  { name: "Ifle", email: "fathimahifla2003@gmail.com" },
  { name: "Samsheena", email: "samsheenam2003@gmail.com" },
  { name: "Shafeeda", email: "mariyam.safeeda.mardala@gmail.com" },
  { name: "Shabna", email: "fathimathshabna2000@gmail.com" },
  { name: "Bavishya", email: "bhavishya.kerpada@gmail.com" },
  { name: "Geetha", email: "geethajyothi2000@gmail.com" },
  { name: "Akhila", email: "akhilanishith84@gmail.com" },
  { name: "Thomas EE", email: "thomas.abraham66.india@gmail.com" },
].map((member) => ({
  ...member,
  teamName: "Suggested",
}));

const formatDateAsYmd = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getEntityId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return String(value._id || value.id || value);
};

/** Individual hosts may book their own private rooms (server allows owner self-booking). */
const hasCompanyWorkspace = (user) =>
  Boolean(getEntityId(user?.company || user?.companyId));

const isOwnerBookingBlocked = (room, user) => {
  if (!room || !user) return false;
  if (isPublicMeetingRoom(room)) return false;
  if (!hasCompanyWorkspace(user)) return false;
  return isMeetingRoomOwnedByUser(room, user) && !isSameCompanyMeetingRoom(room, user);
};

const filterBookableRooms = (roomList, user) => {
  if (!Array.isArray(roomList)) return [];
  return roomList.filter((room) => isMeetingRoomBookableByUser(room, user));
};

const ACTIVE_BOOKING_STATUSES = new Set(["pending", "approved", "confirmed"]);

const isActiveBooking = (booking) =>
  ACTIVE_BOOKING_STATUSES.has(String(booking?.status || "").toLowerCase());

const BookRoom = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? mergeUserWithStaffSession(JSON.parse(raw)) : null;
    } catch {
      return null;
    }
  }, []);
  const currentCompanyId = useMemo(
    () => getEntityId(currentUser?.company || currentUser?.companyId),
    [currentUser]
  );

  // Get URL parameters
  const roomIdFromUrl = searchParams.get("roomId");
  const dateFromUrl = searchParams.get("date");
  const timeFromUrl = searchParams.get("time");

  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(roomIdFromUrl || "");
  const [selectedDate, setSelectedDate] = useState(
    dateFromUrl ? new Date(dateFromUrl) : new Date()
  );
  const [startTime, setStartTime] = useState(timeFromUrl || "");
  const [endTime, setEndTime] = useState("");
  const [purpose, setPurpose] = useState("");
  const [attendees, setAttendees] = useState("");
  const [notes, setNotes] = useState("");
  const [selectedTeam, setSelectedTeam] = useState("");
  const [selectedTeamData, setSelectedTeamData] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedTeamLeaderEmail, setSelectedTeamLeaderEmail] = useState("");
  const [additionalMembers, setAdditionalMembers] = useState([]);
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  const [memberSearchTerm, setMemberSearchTerm] = useState("");
  const [memberAvailability, setMemberAvailability] = useState({});
  const [loading, setLoading] = useState(false);
  const [roomBookings, setRoomBookings] = useState([]);
  const [teams, setTeams] = useState([]);
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  const [addingTeam, setAddingTeam] = useState(false);
  const [errorModal, setErrorModal] = useState({
    show: false,
    message: "",
    type: "error",
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const ADD_TEAM_VALUE = "__add_team__";

  const timeSlots = [
    "09:00",
    "09:30",
    "10:00",
    "10:30",
    "11:00",
    "11:30",
    "12:00",
    "12:30",
    "13:00",
    "13:30",
    "14:00",
    "14:30",
    "15:00",
    "15:30",
    "16:00",
    "16:30",
    "17:00",
    "17:30",
    "18:00",
    "18:30",
    "19:00",
  ];

  // Fetch rooms the user owns or can manage (API + local fallback)
  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setRooms([]);
          return;
        }

        let user = currentUser;
        try {
          const raw = localStorage.getItem("user");
          if (raw) user = JSON.parse(raw);
        } catch {
          /* keep currentUser */
        }

        const [managedRes, publicRes] = await Promise.all([
          roomsAPI.getRooms({
            ownerAddedOnly: true,
            managedOnly: true,
            sort: "latest",
          }),
          roomsAPI.getRooms({
            ownerAddedOnly: true,
            sort: "latest",
          }),
        ]);
        const userId = String(user?._id || user?.id || "");
        const mergedById = new Map();

        [...(Array.isArray(managedRes.data) ? managedRes.data : []), ...(Array.isArray(publicRes.data) ? publicRes.data : [])].forEach((room) => {
          const id = String(room?._id || room?.id || "");
          if (id) mergedById.set(id, room);
        });

        const urlRoomId = String(roomIdFromUrl || "").trim();
        if (/^[a-f0-9]{24}$/i.test(urlRoomId) && !mergedById.has(urlRoomId)) {
          try {
            const singleRes = await roomsAPI.getRoom(urlRoomId);
            const singleRoom = singleRes?.data;
            const singleId = String(singleRoom?._id || singleRoom?.id || "");
            if (singleId) mergedById.set(singleId, singleRoom);
          } catch {
            /* room may be unavailable */
          }
        }

        try {
          const localRooms = JSON.parse(localStorage.getItem("meetingRooms") || "[]");
          localRooms.forEach((local) => {
            const id = String(local?._id || local?.id || "");
            if (!/^[a-f0-9]{24}$/i.test(id) || mergedById.has(id)) return;
            const ownerId = String(local?.hostId || local?.userId || "");
            if (userId && ownerId && ownerId !== userId) return;
            mergedById.set(id, {
              ...local,
              _id: id,
              name: local.name || local.roomName || "Meeting Room",
              visibility: local.visibility || "private",
              userId: local.hostId || local.userId,
            });
          });
        } catch {
          /* ignore local parse errors */
        }

        const merged = Array.from(mergedById.values());
        const bookable = filterBookableRooms(merged, mergeUserWithStaffSession(user));
        setRooms(bookable);
      } catch (error) {
        console.error("Error fetching rooms:", error);
        toast.error("Failed to load rooms");
        setErrorModal({
          show: true,
          message: "Failed to load rooms",
          type: "error",
        });
      }
    };
    fetchRooms();
  }, [currentUser, currentCompanyId, roomIdFromUrl]);

  // Preselect room from URL when rooms are loaded (supports MongoId or legacy office-xxx id)
  useEffect(() => {
    if (!roomIdFromUrl || rooms.length === 0) return;
    const id = roomIdFromUrl.trim();
    if (/^[0-9a-fA-F]{24}$/.test(id)) {
      if (rooms.some(r => r._id === id)) setSelectedRoom(id);
      return;
    }
    const officeMatch = id.match(/^office-([0-9a-fA-F]{24})(?:-(\d+))?$/);
    if (officeMatch) {
      const [, officeId, indexStr] = officeMatch;
      const officeRoomsList = rooms.filter(r => r.isOfficeRoom && String(r.officeRoomId) === officeId);
      const idx = indexStr != null ? parseInt(indexStr, 10) : 0;
      const room = officeRoomsList[idx];
      if (room) setSelectedRoom(room._id);
    }
  }, [roomIdFromUrl, rooms]);

  // Load teams from GET /api/meetings/teams (my-teams first, then all)
  const fetchTeams = useCallback(async () => {
    try {
      const response = await teamsAPI.getMyTeams();
      setTeams(response.data || []);
    } catch (error) {
      try {
        const publicResponse = await teamsAPI.getAllTeams();
        setTeams(publicResponse.data || []);
      } catch (err) {
        console.error("Error fetching teams:", err);
      }
    }
  }, []);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  // âœ… Auto-fill end time when start time is selected
  useEffect(() => {
    if (timeFromUrl && selectedDate && startTime === timeFromUrl) {
      const [hours, minutes] = timeFromUrl.split(":").map(Number);
      const startDate = new Date(selectedDate);
      startDate.setHours(hours, minutes, 0, 0);
      const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
      const endHours = endDate.getHours().toString().padStart(2, "0");
      const endMinutes = endDate.getMinutes().toString().padStart(2, "0");
      setEndTime(`${endHours}:${endMinutes}`);
    }
  }, [selectedDate, timeFromUrl, startTime]);

  // âœ… Fetch room bookings when room or date changes
  const fetchRoomBookings = useCallback(async () => {
    if (!selectedRoom || !selectedDate) {
      console.log("â­ï¸ Skipping fetchRoomBookings - missing room or date", { selectedRoom, selectedDate });
      return;
    }

    try {
      const dateStr = formatDateAsYmd(selectedDate);
      
      // âœ… Validate before API call
      if (!selectedRoom || selectedRoom.trim() === '') {
        console.error("âŒ fetchRoomBookings: selectedRoom is missing or empty");
        return;
      }

      if (!dateStr || dateStr.trim() === '') {
        console.error("âŒ fetchRoomBookings: dateStr is missing or empty");
        return;
      }

      console.log("ðŸ“¤ Fetching room bookings:", { roomId: selectedRoom, date: dateStr });
      const response = await bookingsAPI.getRoomBookings(selectedRoom, dateStr);
      setRoomBookings(response.data || []);
      console.log("âœ… Room bookings fetched successfully:", response.data?.length || 0, "bookings");
    } catch (error) {
      console.error("âŒ Error fetching room bookings:", {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status,
        roomId: selectedRoom,
        date: formatDateAsYmd(selectedDate)
      });
    }
  }, [selectedRoom, selectedDate]);

  useEffect(() => {
    fetchRoomBookings();
  }, [fetchRoomBookings]);

  // âœ… Check member availability when team, date, start/end time change
  const checkMemberAvailability = useCallback(async () => {
    if (!startTime || !endTime) return;

    try {
      const start = new Date(selectedDate);
      const [startHours, startMinutes] = startTime.split(":");
      start.setHours(parseInt(startHours), parseInt(startMinutes), 0, 0);

      const end = new Date(selectedDate);
      const [endHours, endMinutes] = endTime.split(":");
      end.setHours(parseInt(endHours), parseInt(endMinutes), 0, 0);

      // âœ… FIXED: Check ALL members from ALL teams, not just selected team
      const allMembersToCheck = [];
      const seenEmails = new Set();

      teams.forEach((team) => {
        // Add team members
        team.members?.forEach((member) => {
          if (member.email && !seenEmails.has(member.email)) {
            allMembersToCheck.push({
              name: member.name,
              email: member.email,
            });
            seenEmails.add(member.email);
          }
        });
      });

      if (allMembersToCheck.length > 0) {
        const response = await teamsAPI.checkMemberAvailability(
          allMembersToCheck,
          start.toISOString(),
          end.toISOString()
        );
        setMemberAvailability(response.data || {});
      }
    } catch (error) {
      console.error("Error checking member availability:", error);
    }
  }, [teams, startTime, endTime, selectedDate]);

  useEffect(() => {
    if (selectedDate && startTime && endTime && teams.length > 0) {
      checkMemberAvailability();
    }
  }, [checkMemberAvailability, selectedDate, startTime, endTime, teams]);

  // âœ… Real-time conflict checking
  useEffect(() => {
    if (selectedRoom && selectedDate && startTime && endTime) {
      const hasConflict = checkTimeConflict(startTime, endTime);
      if (hasConflict) {
        toast.warning("âš ï¸ This time slot is already booked! Please select a different time.");
      }
    }
  }, [selectedRoom, selectedDate, startTime, endTime, roomBookings]);

  // Handle team selection — auto-select all members; "+ Add Team" opens modal
  const handleTeamSelection = (teamId) => {
    if (teamId === ADD_TEAM_VALUE) {
      setShowAddTeamModal(true);
      setSelectedTeam("");
      setSelectedTeamData(null);
      setSelectedTeamLeaderEmail("");
      return;
    }
    setSelectedTeam(teamId);
    if (teamId) {
      const team = teams.find((t) => t._id === teamId);
      if (team) {
        setSelectedTeamData(team);

        // Auto-select all members
        const allMembers = (team.members || []).map((m) => ({
          name: m.name,
          email: m.email,
        }));
        setSelectedMembers(allMembers);

        const defaultLeaderEmail =
          team.teamLeader?.email || allMembers[0]?.email || "";
        setSelectedTeamLeaderEmail(defaultLeaderEmail);

        // Auto-update attendees count (team members + additional members)
        setAttendees((allMembers.length + additionalMembers.length).toString());
      }
    } else {
      setSelectedTeamData(null);
      setSelectedMembers([]);
      setSelectedTeamLeaderEmail("");
      setMemberAvailability({});
      // Update attendees count to only additional members
      setAttendees(
        additionalMembers.length > 0 ? additionalMembers.length.toString() : ""
      );
    }
  };

  const teamLeaderOptions = (() => {
    const byEmail = new Map();
    (selectedTeamData?.members || []).forEach((m) => {
      if (m?.email) {
        byEmail.set(String(m.email).toLowerCase(), {
          name: m.name,
          email: m.email,
        });
      }
    });
    selectedMembers.forEach((m) => {
      if (m?.email) {
        byEmail.set(String(m.email).toLowerCase(), {
          name: m.name,
          email: m.email,
        });
      }
    });
    additionalMembers.forEach((m) => {
      if (m?.email) {
        byEmail.set(String(m.email).toLowerCase(), {
          name: m.name,
          email: m.email,
        });
      }
    });
    return Array.from(byEmail.values());
  })();

  const selectedTeamLeader =
    teamLeaderOptions.find(
      (m) =>
        String(m.email).toLowerCase() ===
        String(selectedTeamLeaderEmail).toLowerCase()
    ) || null;

  // Add new team via POST /api/meetings/teams; backend requires teamName + at least one member
  const handleAddTeam = async () => {
    const name = (newTeamName || "").trim();
    if (!name) {
      setErrorModal({ show: true, message: "Team name is required.", type: "error" });
      return;
    }
    setAddingTeam(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const members = user.email
        ? [{ name: user.name || user.email, email: user.email }]
        : [{ name: "Creator", email: "creator@placeholder.local" }];
      const response = await teamsAPI.createTeam({ teamName: name, members });
      const newTeam = response.data?.data ?? response.data;
      if (newTeam?._id) {
        setTeams((prev) => [newTeam, ...prev]);
        setSelectedTeam(newTeam._id);
        setSelectedTeamData(newTeam);
        const leaderMember = (newTeam.members || members || [])[0];
        setSelectedMembers(
          (newTeam.members || members || []).map((m) => ({
            name: m.name,
            email: m.email,
          }))
        );
        setSelectedTeamLeaderEmail(leaderMember?.email || "");
        setShowAddTeamModal(false);
        setNewTeamName("");
        setErrorModal({ show: true, message: "Team created and selected.", type: "success" });
      } else {
        setErrorModal({ show: true, message: "Team created but response invalid.", type: "error" });
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to create team.";
      setErrorModal({ show: true, message: msg, type: "error" });
    } finally {
      setAddingTeam(false);
    }
  };

  // âœ… Toggle member selection - Allow selecting busy members (they can be skipped)
  const toggleMemberSelection = (member) => {
    setSelectedMembers((prev) => {
      const newMembers = prev.some((m) => m.email === member.email)
        ? prev.filter((m) => m.email !== member.email)
        : [...prev, member];

      // âœ… Auto-update attendees count when members are toggled (team + additional)
      setAttendees((newMembers.length + additionalMembers.length).toString());

      return newMembers;
    });
  };

  // âœ… Get all unique members from all teams
  const getAllAvailableMembers = () => {
    const allMembers = [];
    const seenEmails = new Set();

    teams.forEach((team) => {
      // Add team members
      team.members?.forEach((member) => {
        if (member.email && !seenEmails.has(member.email)) {
          allMembers.push({
            name: member.name,
            email: member.email,
            teamName: team.teamName,
          });
          seenEmails.add(member.email);
        }
      });
    });

    // âœ… FIXED: Filter out members already selected in team AND already added as additional members
    const selectedMemberEmails = new Set(selectedMembers.map((m) => m.email));
    const additionalMemberEmails = new Set(
      additionalMembers.map((m) => m.email)
    );

    const fromTeams = allMembers.filter(
      (m) =>
        !selectedMemberEmails.has(m.email) &&
        !additionalMemberEmails.has(m.email)
    );

    // Add suggested members (exclude if already in teams or already added)
    const suggestedFiltered = SUGGESTED_MEMBERS.filter(
      (m) =>
        !selectedMemberEmails.has(m.email) &&
        !additionalMemberEmails.has(m.email) &&
        !seenEmails.has(m.email)
    );

    return [...fromTeams, ...suggestedFiltered];
  };

  // âœ… Add additional member
  const addAdditionalMember = (member) => {
    setAdditionalMembers((prev) => [...prev, member]);
    setShowMemberDropdown(false);

    // âœ… Auto-update attendees count
    const totalCount = selectedMembers.length + additionalMembers.length + 1;
    setAttendees(totalCount.toString());
  };

  // âœ… Remove additional member
  const removeAdditionalMember = (memberEmail) => {
    setAdditionalMembers((prev) => {
      const newMembers = prev.filter((m) => m.email !== memberEmail);

      // âœ… Auto-update attendees count
      setAttendees((selectedMembers.length + newMembers.length).toString());

      return newMembers;
    });
  };

  // âœ… Check if time slot is booked â€” FIXED LOGIC
  const SLOT_DURATION_MINUTES = 30;
  const MAX_BOOKING_MINUTES = 240;

  const toSlotDateTime = (time) => {
    if (!time) return null;
    const [hours, minutes] = String(time).split(":").map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
    const slotDate = new Date(selectedDate);
    slotDate.setHours(hours, minutes, 0, 0);
    return slotDate;
  };

  const isTimeSlotBooked = (time) => {
    const slotStart = toSlotDateTime(time);
    if (!slotStart) return true;
    const slotEnd = new Date(slotStart.getTime() + SLOT_DURATION_MINUTES * 60 * 1000);

    return roomBookings.some((booking) => {
      if (!isActiveBooking(booking)) return false;
      const bookingStart = new Date(booking.startTime);
      const bookingEnd = new Date(booking.endTime);
      return slotStart < bookingEnd && slotEnd > bookingStart;
    });
  };

  // âœ… Handle start time change â€” DO NOT auto-set end time
  const handleStartTimeChange = (time) => {
    if (!time) {
      setStartTime("");
      setEndTime("");
      return;
    }

    if (isTimeSlotBooked(time)) {
      toast.error("This slot is already busy. Please choose another time.");
      return;
    }

    const startDate = toSlotDateTime(time);
    if (!startDate) {
      toast.error("Invalid start time");
      return;
    }

    const now = new Date();

    if (startDate < now) {
      toast.error("Cannot book a room for a past time");
      return;
    }

    if (!canStartSlotBeBooked(time)) {
      toast.error("No valid end-time window is available for this start slot.");
      return;
    }

    // Set only start time, user will select end time separately
    setStartTime(time);
    // Clear end time so user can select a new one
    setEndTime("");
  };

  // âœ… Check if selected time range conflicts with existing bookings
  const checkTimeConflict = (start, end) => {
    const startTime = toSlotDateTime(start);
    const endTime = toSlotDateTime(end);
    if (!startTime || !endTime || endTime <= startTime) return true;

    return roomBookings.some((booking) => {
      if (!isActiveBooking(booking)) return false;
      const bookingStart = new Date(booking.startTime);
      const bookingEnd = new Date(booking.endTime);

      // Check if there's any overlap
      return startTime < bookingEnd && endTime > bookingStart;
    });
  };

  // âœ… Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedRoom || !startTime || !endTime || !purpose) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (isOwnerBookingBlocked(selectedRoomData, currentUser)) {
      toast.error("You cannot book your own meeting room.");
      return;
    }

    // Check for time conflicts before submitting
    if (checkTimeConflict(startTime, endTime)) {
      toast.error("This time range is already booked. Please select different times.");
      return;
    }

    const start = new Date(selectedDate);
    const [startHours, startMinutes] = startTime.split(":");
    start.setHours(parseInt(startHours), parseInt(startMinutes), 0, 0);

    const end = new Date(selectedDate);
    const [endHours, endMinutes] = endTime.split(":");
    end.setHours(parseInt(endHours), parseInt(endMinutes), 0, 0);

    const now = new Date();

    if (start < now) {
      toast.error("Cannot book a room for a past time");
      return;
    }

    // Enforce minimum 30 minutes, max 4 hours
    const durationMinutes = (end - start) / (1000 * 60);
    if (durationMinutes < 30) {
      toast.error("Meeting duration must be at least 30 minutes");
      return;
    }
    if (durationMinutes > 240) {
      toast.error("Meeting duration cannot exceed 4 hours");
      return;
    }

    if (end <= start) {
      toast.error("End time must be after start time");
      return;
    }

    // âœ… Enhanced validation before API call
    if (!selectedRoom || selectedRoom.trim() === '') {
      toast.error("Please select a room");
      console.error("âŒ Validation Error: selectedRoom is missing or empty");
      return;
    }


    if (!startTime || startTime.trim() === '') {
      toast.error("Please select a start time");
      console.error("âŒ Validation Error: startTime is missing or empty");
      return;
    }

    if (!endTime || endTime.trim() === '') {
      toast.error("Please select an end time");
      console.error("âŒ Validation Error: endTime is missing or empty");
      return;
    }

    if (!purpose || purpose.trim() === '') {
      toast.error("Please enter a meeting purpose");
      console.error("âŒ Validation Error: purpose is missing or empty");
      return;
    }

    if (!selectedTeam || selectedTeam.trim() === '') {
      toast.error("Please select a team");
      console.error("âŒ Validation Error: selectedTeam is missing or empty");
      return;
    }

    if (!selectedTeamLeaderEmail || !selectedTeamLeader) {
      toast.error("Please select a team leader");
      return;
    }

    // Validate date
    if (!selectedDate || isNaN(selectedDate.getTime())) {
      toast.error("Please select a valid date");
      console.error("âŒ Validation Error: selectedDate is invalid");
      return;
    }

    // Validate start and end Date objects
    if (!start || isNaN(start.getTime())) {
      toast.error("Invalid start time");
      console.error("âŒ Validation Error: start Date is invalid");
      return;
    }

    if (!end || isNaN(end.getTime())) {
      toast.error("Invalid end time");
      console.error("âŒ Validation Error: end Date is invalid");
      return;
    }

    setShowConfirmModal(true);
  };

  // âœ… Actual booking after user confirms
  const handleConfirmBook = async () => {
    setShowConfirmModal(false);

    if (isOwnerBookingBlocked(selectedRoomData, currentUser)) {
      toast.error("You cannot book your own meeting room.");
      return;
    }

    const start = new Date(selectedDate);
    const [startHours, startMinutes] = startTime.split(":");
    start.setHours(parseInt(startHours), parseInt(startMinutes), 0, 0);

    const end = new Date(selectedDate);
    const [endHours, endMinutes] = endTime.split(":");
    end.setHours(parseInt(endHours), parseInt(endMinutes), 0, 0);

    setLoading(true);
    let bookingData = null; // Define outside try block for error logging
    try {
      // Combine team members and additional members
      const allSelectedMembers = [...selectedMembers, ...additionalMembers];

      // âœ… Build booking payload with validation
      let sessionUser = currentUser;
      try {
        const raw = localStorage.getItem("user");
        if (raw) sessionUser = mergeUserWithStaffSession(JSON.parse(raw));
      } catch {
        /* keep currentUser */
      }
      const staffDetails = resolveStaffDetailsForBooking(sessionUser);

      bookingData = {
        room: selectedRoom.trim(),
        startTime: start.toISOString(),
        endTime: end.toISOString(),
        purpose: purpose.trim(),
        team: selectedTeam.trim(),
        teamLeader: {
          name: selectedTeamLeader.name,
          email: selectedTeamLeader.email,
        },
        ...(attendees && attendees.trim() !== '' ? { attendees: parseInt(attendees) } : {}),
        ...(notes && notes.trim() !== '' ? { notes: notes.trim() } : {}),
        ...(allSelectedMembers.length > 0 ? { selectedMembers: allSelectedMembers } : {}),
        ...(staffDetails ? { staffDetails } : {}),
      };

      // âœ… Log booking payload before API call (for debugging)
      console.log("ðŸ“¤ Booking Payload:", {
        room: bookingData.room,
        startTime: bookingData.startTime,
        endTime: bookingData.endTime,
        purpose: bookingData.purpose,
        team: bookingData.team,
        attendees: bookingData.attendees,
        selectedMembersCount: bookingData.selectedMembers?.length || 0,
        hasNotes: !!bookingData.notes
      });

      // âœ… Validate payload before sending
      if (!bookingData.room) {
        throw new Error("Room ID is required");
      }
      if (!bookingData.startTime) {
        throw new Error("Start time is required");
      }
      if (!bookingData.endTime) {
        throw new Error("End time is required");
      }
      if (!bookingData.purpose) {
        throw new Error("Purpose is required");
      }
      if (!bookingData.team) {
        throw new Error("Team is required");
      }

      console.log("âœ… All validations passed, calling API...");
      await bookingsAPI.createBooking(bookingData);

      toast.success("Room booked successfully!");

      // Reset form
      setSelectedRoom("");
      setStartTime("");
      setEndTime("");
      setPurpose("");
      setAttendees("");
      setNotes("");
      setSelectedTeam("");
      setSelectedTeamData(null);
      setSelectedTeamLeaderEmail("");
      setSelectedMembers([]);
      setAdditionalMembers([]);
      setMemberAvailability({});
      setRoomBookings([]);

      const selectedDateYmd = formatDateAsYmd(selectedDate);
      const scheduleMonth = selectedDateYmd.slice(0, 7);
      navigate(
        `/meetings/schedule?date=${encodeURIComponent(selectedDateYmd)}&month=${encodeURIComponent(scheduleMonth)}&roomId=${encodeURIComponent(bookingData.room)}`
      );
    } catch (error) {
      console.error("âŒ Booking Error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        payload: bookingData || "Not created (error occurred before payload creation)"
      });

      let errorMessage = "Failed to book room";
      
      if (error.response?.data) {
        if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
          const validationErrors = error.response.data.errors
            .map(err => err.msg || err.message)
            .join(", ");
          errorMessage = `Validation Error: ${validationErrors}`;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
      
      // Show detailed error in console for debugging
      if (process.env.NODE_ENV === 'development') {
        console.error("Full error details:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Helper: Get filtered time slots (only present and future times)
  const getAvailableTimeSlots = () => {
    const now = new Date();
    const selectedDateStart = new Date(selectedDate);
    selectedDateStart.setHours(0, 0, 0, 0);

    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    // If selected date is today, filter out past times
    if (selectedDateStart.getTime() === todayStart.getTime()) {
      return timeSlots.filter((time) => {
        const slotDate = new Date(selectedDate);
        const [h, m] = time.split(":").map(Number);
        slotDate.setHours(h, m, 0, 0);
        return slotDate >= now;
      });
    }

    // If selected date is in the future, show all time slots
    return timeSlots;
  };

  const canStartSlotBeBooked = (time) => {
    if (isTimeSlotBooked(time)) return false;

    const startIdx = timeSlots.indexOf(time);
    if (startIdx === -1) return false;

    return timeSlots.slice(startIdx + 1).some((candidateEnd) => {
      const startDate = toSlotDateTime(time);
      const endDate = toSlotDateTime(candidateEnd);
      if (!startDate || !endDate || endDate <= startDate) return false;
      const durationMinutes = (endDate - startDate) / (1000 * 60);
      if (durationMinutes < SLOT_DURATION_MINUTES || durationMinutes > MAX_BOOKING_MINUTES) {
        return false;
      }
      return !checkTimeConflict(time, candidateEnd);
    });
  };

  // Helper: Get room name from selected room ID
  const getSelectedRoomName = () => {
    if (!selectedRoom) return '';
    const room = rooms.find(r => r._id === selectedRoom);
    return room ? room.name : '';
  };
  const selectedRoomData = rooms.find((r) => r._id === selectedRoom) || null;
  const bookableRooms = rooms;

  const bookingDurationHours = (() => {
    if (!startTime || !endTime) return 0;
    const [sh, sm] = startTime.split(":").map(Number);
    const [eh, em] = endTime.split(":").map(Number);
    const start = sh * 60 + sm;
    const end = eh * 60 + em;
    if (end <= start) return 0;
    return (end - start) / 60;
  })();
  const bookingTotal = (selectedRoomData?.pricePerHour || 0) * bookingDurationHours;
  const formattedBookingDate = selectedDate
    ? selectedDate.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "-";
  const autoAttendeeCount = selectedMembers.length + additionalMembers.length;
  const displayAttendeeCount = autoAttendeeCount || attendees || "-";
  const roomLocationLabel =
    selectedRoomData?.location ||
    selectedRoomData?.city ||
    "Location updates after selection";

  const selectedRoomImage = useMemo(
    () => getPropertyImage(selectedRoomData, MEETING_ROOM_FALLBACK_IMAGE),
    [selectedRoomData]
  );

  const selectedRoomVisibilityLabel = useMemo(() => {
    if (!selectedRoomData) return "";
    return String(selectedRoomData.visibility || "private").toLowerCase() === "public"
      ? "Public"
      : "Private";
  }, [selectedRoomData]);

  const handleRoomImageError = (event) => {
    event.currentTarget.onerror = null;
    event.currentTarget.src = MEETING_ROOM_FALLBACK_IMAGE;
  };

  const handleAvailabilitySlotClick = (time) => {
    const slotDate = new Date(selectedDate);
    const [h, m] = time.split(":").map(Number);
    slotDate.setHours(h, m, 0, 0);
    if (slotDate < new Date()) return;
    if (isTimeSlotBooked(time)) return;

    if (!startTime || (startTime && endTime)) {
      handleStartTimeChange(time);
      return;
    }

    const startIdx = timeSlots.indexOf(startTime);
    const clickIdx = timeSlots.indexOf(time);
    if (clickIdx <= startIdx) {
      handleStartTimeChange(time);
      return;
    }

    if (checkTimeConflict(startTime, time)) {
      toast.warning("This time conflicts with an existing booking.");
      return;
    }

    setEndTime(time);
  };

  // End-time options must be conflict-free for selected start time
  const getEndTimeSlots = () => {
    if (!startTime) return [];
    const startIdx = timeSlots.indexOf(startTime);
    if (startIdx === -1) return [];

    return timeSlots
      .slice(startIdx + 1)
      .filter((candidateEnd) => {
        const startDate = toSlotDateTime(startTime);
        const endDate = toSlotDateTime(candidateEnd);
        if (!startDate || !endDate || endDate <= startDate) return false;
        const durationMinutes = (endDate - startDate) / (1000 * 60);
        if (durationMinutes < SLOT_DURATION_MINUTES || durationMinutes > MAX_BOOKING_MINUTES) {
          return false;
        }
        return !checkTimeConflict(startTime, candidateEnd);
      });
  };

  useEffect(() => {
    if (!selectedDate || Number.isNaN(new Date(selectedDate).getTime())) return;

    const selectedDateStart = new Date(selectedDate);
    selectedDateStart.setHours(0, 0, 0, 0);
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    // If today's working slots are over, move to tomorrow so dropdown doesn't look empty.
    if (selectedDateStart.getTime() === todayStart.getTime()) {
      const availableSlotsToday = getAvailableTimeSlots().filter((time) =>
        canStartSlotBeBooked(time)
      );
      if (availableSlotsToday.length === 0) {
        const tomorrow = new Date(selectedDateStart);
        tomorrow.setDate(tomorrow.getDate() + 1);
        setSelectedDate(tomorrow);
        setStartTime("");
        setEndTime("");
        toast.info("Today's slots are over. Showing tomorrow's slots.");
      }
    }
  }, [selectedDate]);

  return (
    <div className="min-h-screen page-content book-room-neon-page">
      <div className="book-room-neon-orb book-room-neon-orb-one" aria-hidden="true"></div>
      <div className="book-room-neon-orb book-room-neon-orb-two" aria-hidden="true"></div>
      <div className="book-room-neon-grid-lines" aria-hidden="true"></div>
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200 px-4 py-3 book-room-neon-header">
        <div className="container mx-auto flex items-center justify-between book-room-neon-header-shell">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-gray-700 hover:text-amber-300 transition-colors book-room-neon-back-button"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            <span className="font-medium">Go Back</span>
          </button>
          <h1 className="text-xl font-bold text-gray-900 book-room-neon-page-title">Book Meeting Room</h1>
          <div className="w-20"></div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6 relative z-10 book-room-neon-main">
        <div className="max-w-7xl mx-auto book-room-neon-shell">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 book-room-neon-grid">
            {/* Enhanced Booking Form */}
            <div className="bg-white/90 backdrop-blur-lg rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] p-8 border border-white/60 relative overflow-hidden book-room-neon-panel book-room-neon-panel-form">
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-amber-300/20 to-lime-700/20 rounded-full -translate-y-20 translate-x-20"></div>

              <div className="relative z-10">
                <div className="book-room-neon-panel-head">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-700 text-white rounded-2xl flex items-center justify-center mr-3 shadow-lg book-room-neon-panel-icon">
                    <svg
                      className="w-7 h-7"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3a2 2 0 012-2h8a2 2 0 012 2v4m0 0V7a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V9a2 2 0 012-2h2m8 0V9a2 2 0 00-2-2H8a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V9a2 2 0 00-2-2z"
                      />
                    </svg>
                  </div>
                  <div className="book-room-neon-panel-copy">
                    <span className="book-room-neon-panel-eyebrow">Reservation setup</span>
                    <h2 className="text-2xl font-bold text-gray-900">
                      Booking Details
                    </h2>
                    <p className="book-room-neon-panel-description">
                      Select the room, lock the time, and add attendees in one smooth flow.
                    </p>
                  </div>
                </div>

                {selectedRoomData ? (
                  <div className="book-room-neon-room-preview" key={selectedRoom}>
                    <img
                      src={selectedRoomImage}
                      alt={getSelectedRoomName()}
                      className="book-room-neon-room-preview-photo"
                      loading="lazy"
                      decoding="async"
                      onError={handleRoomImageError}
                    />
                    <div className="book-room-neon-room-preview-overlay">
                      <div className="book-room-neon-room-preview-badge">
                        <svg
                          className="book-room-neon-room-preview-lock"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                          />
                        </svg>
                        <span>
                          {selectedRoomVisibilityLabel} · {getSelectedRoomName()}
                        </span>
                      </div>
                      <p className="book-room-neon-room-preview-meta">
                        {selectedRoomData.capacity ? `${selectedRoomData.capacity} Seats` : "Flexible capacity"}
                        {" · "}
                        {roomLocationLabel}
                      </p>
                    </div>
                  </div>
                ) : null}

                <div className="book-room-neon-quickbar">
                  <div className="book-room-neon-quickbar-item">
                    <span className="book-room-neon-quickbar-label">Date</span>
                    <strong>{formattedBookingDate}</strong>
                  </div>
                  <div className="book-room-neon-quickbar-item">
                    <span className="book-room-neon-quickbar-label">Room</span>
                    <strong>{getSelectedRoomName() || "Choose a room"}</strong>
                  </div>
                  <div className="book-room-neon-quickbar-item">
                    <span className="book-room-neon-quickbar-label">Attendees</span>
                    <strong>{displayAttendeeCount}</strong>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5 book-room-neon-form">
                  {/* Room Selection */}
                  <div className="space-y-2">
                    <label className="block text-sm font-bold text-gray-900 mb-2">
                      Select Room *
                    </label>
                    <div
                      className={`book-room-neon-room-select-wrap${
                        selectedRoomData ? " has-thumb" : ""
                      }`}
                    >
                      {selectedRoomData ? (
                        <div className="book-room-neon-room-select-thumb" aria-hidden="true">
                          <img
                            src={selectedRoomImage}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            onError={handleRoomImageError}
                          />
                        </div>
                      ) : null}
                      <select
                        value={selectedRoom}
                        onChange={(e) => setSelectedRoom(e.target.value)}
                        className="book-room-neon-room-select"
                        required
                      >
                        <option value="">Choose a room</option>
                        {bookableRooms.map((room) => (
                          <option key={room._id} value={room._id}>
                            {room.isOfficeRoom ? "Office - " : ""}
                            {room.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Date Selection */}
                  <div className="space-y-2">
                    <label className="block text-sm font-bold text-gray-900 mb-2">
                      Select Date *
                    </label>
                    <DatePicker
                      selected={selectedDate}
                      onChange={(date) => setSelectedDate(date)}
                      minDate={new Date()}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-200 bg-white/50 backdrop-blur-sm"
                      dateFormat="MMMM d, yyyy"
                      placeholderText="Select date"
                    />
                  </div>

                  {/* Time Selection */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-bold text-gray-900 mb-2">
                        Start Time *
                      </label>
                      <select
                        value={startTime}
                        onChange={(e) => handleStartTimeChange(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-200 bg-white/50 backdrop-blur-sm"
                        required
                      >
                        <option value="">Select start time</option>
                        {getAvailableTimeSlots().filter((time) => canStartSlotBeBooked(time)).length === 0 && (
                          <option value="" disabled>
                            No slots available for selected date
                          </option>
                        )}
                        {getAvailableTimeSlots().map((time) => (
                          <option
                            key={time}
                            value={time}
                            disabled={!canStartSlotBeBooked(time)}
                          >
                            {time}
                            {isTimeSlotBooked(time) ? " (Busy)" : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-bold text-gray-900 mb-2">
                        End Time *
                      </label>
                      <select
                        value={endTime}
                        onChange={(e) => {
                          const newEndTime = e.target.value;
                          setEndTime(newEndTime);

                          // Check for conflicts when end time is changed
                          if (
                            startTime &&
                            newEndTime &&
                            checkTimeConflict(startTime, newEndTime)
                          ) {
                            toast.warning("This time range conflicts with an existing booking. Please select different times.");
                            setEndTime("");
                          }
                        }}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-200 bg-white/50 backdrop-blur-sm"
                        required
                      >
                        <option value="">Select end time</option>
                        {startTime && getEndTimeSlots().length === 0 && (
                          <option value="" disabled>
                            No end-time slots available
                          </option>
                        )}
                        {getEndTimeSlots().map((time) => (
                          <option key={time} value={time}>
                            {time}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Team Selection */}
                  <div className="space-y-2">
                    <label className="block text-sm font-bold text-gray-900 mb-2">
                      Select Team
                    </label>
                    <select
                      value={selectedTeam}
                      onChange={(e) => handleTeamSelection(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-200 bg-white/50 backdrop-blur-sm"
                    >
                      <option value="">No team selected</option>
                      {teams.map((team) => (
                        <option key={team._id} value={team._id}>
                          {team.teamName} ({team.members?.length || 0} members)
                        </option>
                      ))}
                      <option value={ADD_TEAM_VALUE}>+ Add Team</option>
                    </select>
                  </div>

                  {/* Team Leader Selection */}
                  {selectedTeamData && (
                    <div className="space-y-2">
                      <label className="block text-sm font-bold text-gray-900 mb-2">
                        Select Team Leader <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={selectedTeamLeaderEmail}
                        onChange={(e) => setSelectedTeamLeaderEmail(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-200 bg-white/50 backdrop-blur-sm"
                      >
                        <option value="">Choose team leader</option>
                        {teamLeaderOptions.map((member) => (
                          <option key={member.email} value={member.email}>
                            {member.name}
                            {member.email ? ` (${member.email})` : ""}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500">
                        This name will appear as Team Leader in booking details.
                      </p>
                    </div>
                  )}

                  {/* Team Members */}
                  {selectedTeamData && (
                    <div className="space-y-4 -mx-4 sm:mx-0 px-4 sm:px-0">
                      <label className="block text-sm font-bold text-gray-900 mb-2">
                        Select Team Members
                      </label>
                      <div className="space-y-3 max-h-64 overflow-y-auto">
                        {(selectedTeamData.members || []).map((member, index) => {
                          const isLeader =
                            String(member.email || "").toLowerCase() ===
                            String(selectedTeamLeaderEmail || "").toLowerCase();
                          const isSelected = selectedMembers.some(
                            (m) => m.email === member.email
                          );
                          const isBusy =
                            memberAvailability[member.email]?.isAvailable ===
                            false;
                          const conflicts =
                            memberAvailability[member.email]?.conflicts || [];

                          return (
                            <div
                              key={`${member.email}-${index}`}
                              className={`flex items-center space-x-3 p-4 border-2 rounded-xl transition-all duration-200 ${
                                isSelected
                                  ? "border-blue-300 bg-blue-50"
                                  : isBusy
                                  ? "border-red-200 bg-red-50"
                                  : "border-gray-200 bg-white/50"
                              }`}
                            >
                              <input
                                type="checkbox"
                                id={`member-${member.email}-${index}`}
                                checked={isSelected}
                                onChange={() => toggleMemberSelection(member)}
                                className="w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                              />
                              <label
                                htmlFor={`member-${member.email}-${index}`}
                                className="flex-1 cursor-pointer"
                              >
                                <div className="w-full">
                                  <div className="flex items-center space-x-2">
                                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">
                                      {String(member.name)
                                        .charAt(0)
                                        .toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="font-semibold text-gray-900 break-words">
                                        {String(member.name)}
                                      </p>
                                    </div>
                                    {isLeader && (
                                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full font-medium whitespace-nowrap">
                                        Leader
                                      </span>
                                    )}
                                    {isBusy ? (
                                      <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full font-medium whitespace-nowrap">
                                        Busy
                                      </span>
                                    ) : (
                                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full font-medium whitespace-nowrap">
                                        Free
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {isBusy && conflicts.length > 0 && (
                                  <div className="mt-2 text-sm text-red-600 bg-red-100 p-2 rounded-lg">
                                    {conflicts.map((conflict, idx) => (
                                      <div key={idx}>
                                        ðŸ“… {conflict.meetingName} at{" "}
                                        {conflict.roomName?.name ||
                                          conflict.roomName}
                                        <br />
                                        ðŸ•{" "}
                                        {new Date(
                                          conflict.startTime
                                        ).toLocaleTimeString()}{" "}
                                        -{" "}
                                        {new Date(
                                          conflict.endTime
                                        ).toLocaleTimeString()}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                      {selectedMembers.length > 0 && (
                        <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                          <p className="text-sm text-green-800 font-medium">
                            âœ… {selectedMembers.length} team member(s) selected
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Add Additional Members */}
                  <div className="space-y-3">
                    <label className="block text-sm font-bold text-gray-900 mb-2">
                      Add Additional Members
                      <span className="text-gray-500 text-xs font-normal ml-2">
                        (Optional - Add members from other teams)
                      </span>
                    </label>

                    {/* Add Member Button */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() =>
                          setShowMemberDropdown(!showMemberDropdown)
                        }
                        className="w-full px-4 py-3 border-2 border-dashed border-blue-300 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 flex items-center justify-center space-x-2 text-blue-600 font-medium"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                        <span>Add Member</span>
                      </button>

                      {/* Member Dropdown */}
                      {showMemberDropdown && (
                        <div className="absolute z-50 w-full mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-2xl max-h-80 overflow-hidden">
                          {/* Search Bar */}
                          <div className="sticky top-0 bg-white p-3 border-b border-gray-200">
                            <div className="relative">
                              <svg
                                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0118 0z"
                                />
                              </svg>
                              <input
                                type="text"
                                placeholder="Search members..."
                                value={memberSearchTerm}
                                onChange={(e) =>
                                  setMemberSearchTerm(e.target.value)
                                }
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm"
                                autoFocus
                              />
                            </div>
                          </div>

                          {/* Members List */}
                          <div className="max-h-64 overflow-y-auto">
                            {getAllAvailableMembers().filter(
                              (member) =>
                                member.name
                                  .toLowerCase()
                                  .includes(memberSearchTerm.toLowerCase()) ||
                                String(member.email || "")
                                  .toLowerCase()
                                  .includes(memberSearchTerm.toLowerCase()) ||
                                member.teamName
                                  .toLowerCase()
                                  .includes(memberSearchTerm.toLowerCase())
                            ).length > 0 ? (
                              getAllAvailableMembers()
                                .filter(
                                  (member) =>
                                    member.name
                                      .toLowerCase()
                                      .includes(
                                        memberSearchTerm.toLowerCase()
                                      ) ||
                                    String(member.email || "")
                                      .toLowerCase()
                                      .includes(
                                        memberSearchTerm.toLowerCase()
                                      ) ||
                                    member.teamName
                                      .toLowerCase()
                                      .includes(memberSearchTerm.toLowerCase())
                                )
                                .map((member, index) => {
                                  const isBusy =
                                    memberAvailability[member.email]
                                      ?.isAvailable === false;
                                  const conflicts =
                                    memberAvailability[member.email]
                                      ?.conflicts || [];

                                  return (
                                    <button
                                      key={`${member.email}-${index}`}
                                      type="button"
                                      onClick={() => {
                                        addAdditionalMember(member);
                                        setMemberSearchTerm("");
                                      }}
                                      className={`w-full px-4 py-3 transition-colors text-left border-b border-gray-100 last:border-b-0 ${
                                        isBusy
                                          ? "bg-red-50 hover:bg-red-100"
                                          : "hover:bg-blue-50"
                                      }`}
                                    >
                                      <div className="flex items-center space-x-3">
                                        <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">
                                          {member.name.charAt(0).toUpperCase()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className="font-semibold text-gray-900 truncate">
                                            {member.name}
                                          </p>
                                          <p className="text-xs text-gray-500 truncate">
                                            {member.email || member.teamName}
                                          </p>
                                          {member.email && member.teamName ? (
                                            <p className="text-[11px] text-gray-400 truncate">
                                              {member.teamName} · notified by email
                                            </p>
                                          ) : null}
                                          {isBusy && conflicts.length > 0 && (
                                            <div className="mt-1 text-xs text-red-600">
                                              {conflicts.map(
                                                (conflict, idx) => (
                                                  <div
                                                    key={idx}
                                                    className="truncate"
                                                  >
                                                    ðŸ“… {conflict.meetingName} at{" "}
                                                    {conflict.roomName?.name ||
                                                      conflict.roomName}
                                                  </div>
                                                )
                                              )}
                                            </div>
                                          )}
                                        </div>
                                        {isBusy ? (
                                          <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full font-medium whitespace-nowrap">
                                            Busy
                                          </span>
                                        ) : (
                                          <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full font-medium whitespace-nowrap">
                                            Free
                                          </span>
                                        )}
                                      </div>
                                    </button>
                                  );
                                })
                            ) : (
                              <div className="px-4 py-6 text-center text-gray-500">
                                <svg
                                  className="w-12 h-12 mx-auto mb-2 text-gray-300"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                                  />
                                </svg>
                                <p className="text-sm">
                                  {memberSearchTerm
                                    ? "No members found"
                                    : "No more members available"}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Display Added Additional Members */}
                    {additionalMembers.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs text-gray-600 font-medium">
                          Additional Members Added:
                        </p>
                        {additionalMembers.map((member, index) => {
                          const isBusy =
                            memberAvailability[member.email]?.isAvailable ===
                            false;
                          const conflicts =
                            memberAvailability[member.email]?.conflicts || [];

                          return (
                            <div
                              key={`additional-${member.email}-${index}`}
                              className={`flex items-center space-x-3 p-3 border-2 rounded-xl ${
                                isBusy
                                  ? "border-red-200 bg-red-50"
                                  : "border-purple-200 bg-purple-50"
                              }`}
                            >
                              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">
                                {member.name.charAt(0).toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-gray-900 text-sm">
                                  {member.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {member.teamName}
                                </p>
                                {isBusy && conflicts.length > 0 && (
                                  <div className="mt-1 text-xs text-red-600">
                                    {conflicts.map((conflict, idx) => (
                                      <div key={idx}>
                                        ðŸ“… {conflict.meetingName} at{" "}
                                        {conflict.roomName?.name ||
                                          conflict.roomName}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              {isBusy ? (
                                <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full font-medium whitespace-nowrap">
                                  Busy
                                </span>
                              ) : (
                                <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full font-medium whitespace-nowrap">
                                  Free
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() =>
                                  removeAdditionalMember(member.email)
                                }
                                className="p-1 hover:bg-red-100 rounded-full transition-colors"
                                title="Remove member"
                              >
                                <svg
                                  className="w-5 h-5 text-red-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            </div>
                          );
                        })}
                        <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                          <p className="text-sm text-purple-800 font-medium">
                            âœ… {additionalMembers.length} additional member(s)
                            added
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Attendees */}
                  <div className="space-y-2">
                    <label className="block text-sm font-bold text-gray-900 mb-2">
                      Number of Attendees
                      {(selectedMembers.length > 0 ||
                        additionalMembers.length > 0) && (
                        <span className="text-green-600 text-xs font-normal ml-2">
                          (Auto-calculated: {selectedMembers.length} team +{" "}
                          {additionalMembers.length} additional)
                        </span>
                      )}
                    </label>
                    <input
                      type="number"
                      value={attendees}
                      onChange={(e) => setAttendees(e.target.value)}
                      className={`w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-200 bg-white/50 backdrop-blur-sm ${
                        selectedMembers.length > 0 ||
                        additionalMembers.length > 0
                          ? "border-green-300 bg-green-50/50"
                          : "border-gray-200"
                      }`}
                      placeholder="Number of people"
                      min="1"
                      readOnly={
                        selectedMembers.length > 0 ||
                        additionalMembers.length > 0
                      }
                    />
                    {(selectedMembers.length > 0 ||
                      additionalMembers.length > 0) && (
                      <p className="text-xs text-green-600 flex items-center">
                        <svg
                          className="w-4 h-4 mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        Total:{" "}
                        {selectedMembers.length + additionalMembers.length}{" "}
                        members
                      </p>
                    )}
                  </div>

                  {/* Purpose */}
                  <div className="space-y-2">
                    <label className="block text-sm font-bold text-gray-900 mb-2">
                      Meeting Purpose *
                    </label>
                    <input
                      type="text"
                      value={purpose}
                      onChange={(e) => setPurpose(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-200 bg-white/50 backdrop-blur-sm"
                      placeholder="e.g., Team meeting, general meeting"
                      required
                    />
                  </div>

                  {/* Notes */}
                  <div className="space-y-2">
                    <label className="block text-sm font-bold text-gray-900 mb-2">
                      Notes (Optional)
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-200 bg-white/50 backdrop-blur-sm"
                      placeholder="Additional notes..."
                      rows="3"
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="w-full px-8 py-4 bg-gradient-to-r from-amber-700 to-lime-700 text-white font-bold text-lg rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Booking Room...</span>
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3a2 2 0 012-2h8a2 2 0 012 2v4m0 0V7a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V9a2 2 0 012-2h2m8 0V9a2 2 0 00-2-2H8a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V9a2 2 0 00-2-2z"
                          />
                        </svg>
                        <span>Book Room</span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* Enhanced Room Availability */}
            <div className="bg-white/90 backdrop-blur-lg rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] p-8 border border-white/60 relative overflow-hidden book-room-neon-panel book-room-neon-panel-side">
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-green-400/20 to-emerald-600/20 rounded-full -translate-y-20 translate-x-20"></div>

              <div className="relative z-10">
                <div className="book-room-neon-panel-head">
                  <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-2xl flex items-center justify-center mr-3 shadow-lg book-room-neon-panel-icon">
                    <svg
                      className="w-7 h-7"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="book-room-neon-panel-copy">
                    <span className="book-room-neon-panel-eyebrow">Live calendar</span>
                    <h2 className="text-2xl font-bold text-gray-900">
                      Room Availability
                    </h2>
                    <p className="book-room-neon-panel-description">
                      Review open slots and your live booking estimate before you confirm.
                    </p>
                  </div>
                </div>
                <div className="book-room-neon-summary-card">
                  <h3>Booking Summary</h3>
                  {selectedRoomData ? (
                    <div className="book-room-neon-summary-media" key={selectedRoom}>
                      <img
                        src={selectedRoomImage}
                        alt={getSelectedRoomName()}
                        className="book-room-neon-summary-image"
                        loading="lazy"
                        decoding="async"
                        onError={handleRoomImageError}
                      />
                      <div className="book-room-neon-summary-media-body">
                        <div className="book-room-neon-summary-row">
                          <span>Room</span>
                          <strong>{getSelectedRoomName()}</strong>
                        </div>
                        <div className="book-room-neon-summary-row">
                          <span>Date</span>
                          <strong>{formattedBookingDate}</strong>
                        </div>
                        <div className="book-room-neon-summary-row">
                          <span>Time</span>
                          <strong>
                            {startTime && endTime ? `${startTime} - ${endTime}` : "-"}
                          </strong>
                        </div>
                        <div className="book-room-neon-summary-row">
                          <span>Duration</span>
                          <strong>
                            {bookingDurationHours > 0
                              ? `${bookingDurationHours === 1 ? "1 hour" : `${bookingDurationHours.toFixed(1)}h`}`
                              : "-"}
                          </strong>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="book-room-neon-summary-row">
                        <span>Room</span>
                        <strong>-</strong>
                      </div>
                      <div className="book-room-neon-summary-row">
                        <span>Date</span>
                        <strong>{formattedBookingDate}</strong>
                      </div>
                      <div className="book-room-neon-summary-row">
                        <span>Time</span>
                        <strong>-</strong>
                      </div>
                      <div className="book-room-neon-summary-row">
                        <span>Duration</span>
                        <strong>-</strong>
                      </div>
                    </>
                  )}
                  <div className="book-room-neon-summary-total">
                    <span>Price</span>
                    <strong>
                      {selectedRoomData?.pricePerHour
                        ? `₹${selectedRoomData.pricePerHour} / hour`
                        : bookingTotal > 0
                        ? `₹${Math.round(bookingTotal)}`
                        : "-"}
                    </strong>
                  </div>
                </div>

                <div className="book-room-neon-room-meta">
                  <span className="book-room-neon-room-chip">
                    {selectedRoomData?.capacity ? `${selectedRoomData.capacity} seats` : "Capacity on selection"}
                  </span>
                  <span className="book-room-neon-room-chip">
                    {selectedRoomData?.pricePerHour ? `₹${selectedRoomData.pricePerHour}/hour` : "Rate updates live"}
                  </span>
                  <span className="book-room-neon-room-chip">{roomLocationLabel}</span>
                </div>

                {selectedRoom ? (
                  <div className="book-room-neon-slots-section">
                    <div className="book-room-neon-availability-note">
                      <p className="book-room-neon-note-text">
                        Available time slots for{" "}
                        <strong>{getSelectedRoomName() || "Selected Room"}</strong> on{" "}
                        <strong>{formattedBookingDate}</strong>
                      </p>
                      <p className="book-room-neon-note-hint">
                        Tap an open slot to set start time, then tap a later slot for end time.
                      </p>
                    </div>

                    <div className="book-room-neon-slots-panel">
                      <div className="book-room-neon-slots">
                        {timeSlots.map((time) => {
                          const slotDate = new Date(selectedDate);
                          const [h, m] = time.split(":").map(Number);
                          slotDate.setHours(h, m, 0, 0);

                          const isPast = slotDate < new Date();
                          const isBooked = isTimeSlotBooked(time);
                          const isOpen = !isPast && !isBooked;
                          const isStart = startTime === time;
                          const isEnd = endTime === time;
                          const isSelected = isStart || isEnd;

                          let slotStateClass = "book-room-neon-slot-open";
                          let label = "Open";

                          if (isPast) {
                            slotStateClass = "book-room-neon-slot-past";
                            label = "Past";
                          } else if (isBooked) {
                            slotStateClass = "book-room-neon-slot-booked";
                            label = "Busy";
                          }

                          const className = [
                            "book-room-neon-slot-card",
                            slotStateClass,
                            isSelected ? "book-room-neon-slot-selected" : "",
                            isOpen ? "book-room-neon-slot-clickable" : "",
                          ]
                            .filter(Boolean)
                            .join(" ");

                          const content = (
                            <>
                              <span className="book-room-neon-slot-time">{time}</span>
                              <span className="book-room-neon-slot-footer">
                                <span className="book-room-neon-slot-label">{label}</span>
                                {isStart ? (
                                  <span className="book-room-neon-slot-tag">Start</span>
                                ) : null}
                                {isEnd ? (
                                  <span className="book-room-neon-slot-tag book-room-neon-slot-tag-end">
                                    End
                                  </span>
                                ) : null}
                              </span>
                            </>
                          );

                          if (isOpen) {
                            return (
                              <button
                                key={time}
                                type="button"
                                className={className}
                                onClick={() => handleAvailabilitySlotClick(time)}
                                aria-pressed={isSelected}
                                aria-label={`${time} open`}
                              >
                                {content}
                              </button>
                            );
                          }

                          return (
                            <div key={time} className={className} aria-disabled="true">
                              {content}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="book-room-neon-legend">
                      <div className="book-room-neon-legend-item">
                        <span className="book-room-neon-legend-swatch book-room-neon-legend-open" />
                        <span>Open</span>
                      </div>
                      <div className="book-room-neon-legend-item">
                        <span className="book-room-neon-legend-swatch book-room-neon-legend-booked" />
                        <span>Busy</span>
                      </div>
                      <div className="book-room-neon-legend-item">
                        <span className="book-room-neon-legend-swatch book-room-neon-legend-past" />
                        <span>Past</span>
                      </div>
                      <div className="book-room-neon-legend-item book-room-neon-legend-item-selected">
                        <span className="book-room-neon-legend-swatch book-room-neon-legend-selected" />
                        <span>Selected</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-16 book-room-neon-empty">
                    <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6 book-room-neon-empty-icon">
                      <svg
                        className="w-12 h-12 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                    </div>
                    <p className="text-lg font-medium book-room-neon-empty-title">
                      Select a room to book
                    </p>
                    <p className="text-sm mt-2 book-room-neon-empty-text">
                      Choose a room from the form to see available time slots
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setShowConfirmModal(false)}>
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 space-y-5" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-700 rounded-2xl flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Confirm Booking</h3>
                <p className="text-sm text-gray-500">Please review your booking details</p>
              </div>
            </div>

            {/* Details */}
            <div className="bg-gray-50 rounded-2xl p-4 space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-sm text-gray-500 font-medium">Room</span>
                <span className="text-sm font-bold text-gray-900">{getSelectedRoomName()}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-sm text-gray-500 font-medium">Date</span>
                <span className="text-sm font-bold text-gray-900">
                  {selectedDate.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-sm text-gray-500 font-medium">Time</span>
                <span className="text-sm font-bold text-gray-900">{startTime} - {endTime}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-sm text-gray-500 font-medium">Purpose</span>
                <span className="text-sm font-bold text-gray-900 text-right max-w-[60%]">{purpose}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-sm text-gray-500 font-medium">Team</span>
                <span className="text-sm font-bold text-gray-900">
                  {teams.find(t => t._id === selectedTeam)?.teamName || selectedTeam}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-sm text-gray-500 font-medium">Team Leader</span>
                <span className="text-sm font-bold text-gray-900">
                  {selectedTeamLeader?.name || "Not selected"}
                </span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-gray-500 font-medium">Attendees</span>
                <span className="text-sm font-bold text-gray-900">
                  {displayAttendeeCount}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 px-4 py-3 rounded-2xl border-2 border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={handleConfirmBook}
                className="flex-1 px-4 py-3 rounded-2xl bg-gradient-to-r from-amber-700 to-lime-700 text-white font-bold hover:shadow-lg transition-all"
              >
                Confirm Booking
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Team modal */}
      {showAddTeamModal && (
        <div
          className="book-room-add-team-backdrop"
          onClick={() => !addingTeam && setShowAddTeamModal(false)}
          role="presentation"
        >
          <div
            className="book-room-add-team-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="book-room-add-team-title"
          >
            <button
              type="button"
              className="book-room-add-team-close"
              onClick={() => !addingTeam && (setShowAddTeamModal(false), setNewTeamName(""))}
              disabled={addingTeam}
              aria-label="Close"
            >
              ×
            </button>

            <div className="book-room-add-team-header">
              <div className="book-room-add-team-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <div>
                <h3 id="book-room-add-team-title" className="book-room-add-team-title">
                  Add Team
                </h3>
                <p className="book-room-add-team-subtitle">
                  Create a team for this booking
                </p>
              </div>
            </div>

            <div className="book-room-add-team-note">
              <span className="book-room-add-team-note-icon" aria-hidden="true">i</span>
              <p>
                Enter a team name below. You will be added automatically as the first member.
              </p>
            </div>

            <div className="book-room-add-team-field">
              <label htmlFor="book-room-new-team-name" className="book-room-add-team-label">
                Team name
              </label>
              <input
                id="book-room-new-team-name"
                type="text"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newTeamName.trim() && !addingTeam) {
                    e.preventDefault();
                    handleAddTeam();
                  }
                }}
                placeholder="e.g. Marketing, Engineering, Sales"
                className="book-room-add-team-input"
                autoFocus
                disabled={addingTeam}
              />
            </div>

            <div className="book-room-add-team-actions">
              <button
                type="button"
                onClick={() => !addingTeam && (setShowAddTeamModal(false), setNewTeamName(""))}
                className="book-room-add-team-btn book-room-add-team-btn--ghost"
                disabled={addingTeam}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddTeam}
                disabled={addingTeam || !newTeamName.trim()}
                className="book-room-add-team-btn book-room-add-team-btn--primary"
              >
                {addingTeam ? "Adding…" : "Add Team"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error/Success toast */}
      {errorModal.show && (
        <div className={`fixed bottom-4 right-4 z-50 px-4 py-3 rounded-xl shadow-lg ${errorModal.type === "success" ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
          {errorModal.message}
          <button onClick={() => setErrorModal((p) => ({ ...p, show: false }))} className="ml-2 underline">Dismiss</button>
        </div>
      )}
    </div>
  );
};

export default BookRoom;

